import { Component, type ErrorInfo, type ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { UiLang } from "../lib/types";

/**
 * Én ødelagt del skal ikke ta med seg hele planen.
 *
 * ── Hvorfor dette manglet, og hva det kostet ───────────────────────────────
 *
 * Meldt fra bruk: «de planla en rute, og da de rullet ned til kartet krasjet
 * det liksom og lastet hjemskjermen på nytt».
 *
 * Appen hadde ingen feilgrense i det hele tatt. I React 18 betyr det at en
 * eneste feil kastet under render avmonterer **hele treet** — ikke bare
 * panelet som feilet. Skjermen blir blank. Brukeren laster på nytt, og har
 * mistet planen de nettopp lagde.
 *
 * Den konkrete feilen var kartets kodebit som forsvant ved en utrulling (se
 * lib/chunk.ts), og den er rettet der. Men det er ikke *derfor* denne fila
 * finnes. Den finnes fordi kostnaden ved en hvilken som helst framtidig feil
 * var å miste alt: ett stopp uten koordinater, ett varsel som mangler et felt,
 * én tredjepart som svarer noe uventet — og turen er borte.
 *
 * ── Hvorfor den brukes flere steder, ikke bare rundt hele appen ────────────
 *
 * En grense rundt alt gjør en blank side om til en feilmelding. Det er bedre,
 * men brukeren har fortsatt mistet planen.
 *
 * En grense rundt *hvert tunge panel* gjør noe helt annet: kartet kan feile
 * mens dagene, etappene og budsjettet står. Brukeren mister et kart og beholder
 * turen sin. Det er forskjellen mellom en app som er i stykker og en app som
 * mangler et kart.
 *
 * ── Hvorfor `resetKeys` ────────────────────────────────────────────────────
 *
 * En feilgrense som har utløst blir stående utløst. Uten en vei tilbake ville
 * et kart som feilet på én plan vært borte for resten av økten — også etter at
 * brukeren lagde en ny plan der det samme aldri ville skjedd. Endres en av
 * nøklene, prøver grensa på nytt.
 */

interface Props {
  children: ReactNode;
  /**
   * Navnet i konsollen, og i feilmeldingen brukeren ser.
   *
   * «Kartet» sier noe. «Component threw» sier ingenting til den som ser det.
   */
  label: string;
  lang: UiLang;
  /** Endres en av disse, prøver grensa på nytt. Se doc-kommentaren. */
  resetKeys?: readonly unknown[];
  /** Er delen liten nok til at en hel feilboks er verre enn ingenting? */
  quiet?: boolean;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * Til konsollen, med navnet på delen som falt ut. Uten navnet er et
     * stabelspor fra en minifisert bunt nesten ikke til å plassere.
     */
    console.error(`[${this.props.label}] falt ut:`, error, info.componentStack);
  }

  componentDidUpdate(previous: Props): void {
    if (!this.state.error) return;
    const before = previous.resetKeys;
    const now = this.props.resetKeys;
    if (!before || !now) return;
    const changed =
      before.length !== now.length || now.some((key, index) => !Object.is(key, before[index]));
    if (changed) this.setState({ error: null });
  }

  private retry = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const t = dictionaries[this.props.lang];

    /*
     * En liten del sier fra på én linje. En feilboks med overskrift og knapp
     * der det sto en merkelapp, er mer i veien enn det den erstatter.
     */
    if (this.props.quiet) {
      return <p className="boundary-quiet">{t.sectionFailed(this.props.label)}</p>;
    }

    return (
      <div className="boundary" role="alert">
        <p className="boundary-what">
          <TriangleAlert size={15} />
          {t.sectionFailed(this.props.label)}
        </p>
        {/*
          «Resten av turen står» er det viktigste i denne boksen. Den som ser en
          feilmelding antar at alt er ødelagt — her er det nettopp ikke tilfellet,
          og det er hele grunnen til at grensa finnes.
        */}
        <p className="boundary-rest">{t.sectionFailedRest}</p>
        <button type="button" className="boundary-retry" onClick={this.retry}>
          {t.sectionFailedRetry}
        </button>
      </div>
    );
  }
}
